import * as React from 'react';
import { useRef } from 'react';
import { observer } from 'mobx-react';
import { useSystem } from '../../../hooks/useSystem';
import { FormikContext, useFormik } from 'formik';
import { FormLayoutWidget } from '../../../widgets/layout/FormLayoutWidget';
import { FormikInputWidget } from '../../../widgets/inputs/FormikInput';
import { GroupedButtons } from '../../../widgets/buttons/GroupedButtons';
import { styled } from '../../../theme';
import { useOrgStore } from '../../../hooks/useOrgStore';
import { CommonInputCss, InputWrapperWidget } from '../../../widgets/inputs';
import * as accounts from '@journeyapps-platform/types-accounts-hub';
import { SmartProjectSelectWidget } from '../../../widgets-smart/SmartProjectSelectWidget';
import { SelectWidget } from '../../../widgets/select/SelectWidget';
import { CenterFormOrientation, CenterFormWidget } from '../../../widgets/layout/CenterFormWidget';
import { ButtonType } from '../../../widgets/buttons/ButtonWidget';
import { JourneyNavigationHandler, Routes } from '../../../routes';
import { ENV } from '../../../env';
import { CustomSeverityLevel, TicketStatus } from '@journeyapps-platform/types-intercom';
import { TicketReplyInputWidget, TicketReplyValue } from './TicketReplyInputWidget';

const SEVERITY_LEVEL_LABELS = {
  [CustomSeverityLevel.MEDIUM]: 'Severity 4 (Medium) - General enquiries',
  [CustomSeverityLevel.HIGH]: 'Severity 3 (High) - Non-critical production issues',
  [CustomSeverityLevel.URGENT]: 'Severity 2 (Urgent) - Severely reduced production functionality',
  [CustomSeverityLevel.CRITICAL]: 'Severity 1 (Critical) - Emergency with Production data / application / systems'
};

const SEVERITY_LEVELS = [
  CustomSeverityLevel.MEDIUM,
  CustomSeverityLevel.HIGH,
  CustomSeverityLevel.URGENT,
  CustomSeverityLevel.CRITICAL
];

export interface SupportPageDialogProps {
  handler: JourneyNavigationHandler<Routes.SUPPORT_CREATE>;
  cancel: () => any;
  success: () => any;
}

export const SupportPageDialog: React.FC<SupportPageDialogProps> = observer((props) => {
  const system = useSystem();
  const orgStore = useOrgStore();
  const org = orgStore.current_org!;
  let ref = useRef({ attachments: [], markdown: '' });

  const formik = useFormik<{
    subject: string;
    severity?: CustomSeverityLevel;
    project?: accounts.SerializedAppResource;
    comment: TicketReplyValue;
  }>({
    initialValues: {
      subject: '',
      comment: {
        markdown: '',
        attachments: []
      }
    },
    validateOnChange: false,
    onSubmit: async ({ subject, comment, severity, project }) => {
      if (!org) {
        return;
      }

      try {
        let tokens = [];
        for (let attachment of comment.attachments) {
          let res = await system.clientProvisioner.provisionIntercomClient().uploadAttachment({
            file: attachment,
            org_id: org.org_id,
            headers: await system.clientProvisioner.standardHeaders()
          });
          tokens.push(res.token);
        }

        await system.clientProvisioner.provisionIntercomClient().createTicket({
          org_id: org.org_id,
          subject,
          user_id: ENV.USER_ID,
          severity: severity!,
          status: TicketStatus.OPEN,
          project_id: project!.id,
          content: comment.markdown,
          attachment_tokens: tokens
        });

        props.success();
      } catch (ex) {
        await system.dialogStore.errorDialog({
          title: 'Something went wrong',
          errorMessage: 'The support ticket was not created. Check console for more details.'
        });
      }
    },
    validateOnBlur: false,
    validate: (values) => {
      let errors: any = {};

      if (!values.subject || values.subject.trim() === '') {
        errors.subject = 'Subject must be set';
      }
      if (!values.project) {
        errors.project = 'Project must be set';
      }
      if (!values.severity) {
        errors.severity = 'Severity must be set';
      }
      return errors;
    }
  });

  return (
    <CenterFormWidget
      noLogo={true}
      primaryLabel="How can we help"
      secondaryLabel={`Please share as much information as you can to help us diagnose the problem faster.`}
      orientation={CenterFormOrientation.ROW}
    >
      <S.Container>
        <FormikContext.Provider value={formik}>
          <FormLayoutWidget
            sections={[
              {
                key: 'details',
                elements: [
                  <S.FormElements>
                    <FormikInputWidget name="subject" label="Subject" />
                    <InputWrapperWidget label="Which project is affected?" error={formik.getFieldMeta('project').error}>
                      <SmartProjectSelectWidget
                        project={formik.values.project}
                        onChange={(selected) => {
                          formik.setFieldValue('project', selected);
                        }}
                      />
                    </InputWrapperWidget>
                    <InputWrapperWidget label="Severity" error={formik.getFieldMeta('severity').error}>
                      <SelectWidget
                        onChange={(value: any) => {
                          formik.setFieldValue('severity', value.value);
                        }}
                        options={SEVERITY_LEVELS.map((l, index) => {
                          return {
                            value: l,
                            label: SEVERITY_LEVEL_LABELS[l]
                          };
                        })}
                      />
                    </InputWrapperWidget>
                    <InputWrapperWidget label="Details" error={false}>
                      <S.TicketReplyContainer>
                        <TicketReplyInputWidget valueRef={ref}></TicketReplyInputWidget>
                      </S.TicketReplyContainer>
                    </InputWrapperWidget>
                  </S.FormElements>
                ]
              },
              {
                key: 'submit',
                elements: [
                  <GroupedButtons
                    btns={[
                      {
                        label: 'Cancel',
                        disabled: false,
                        action: async (event, loading) => {
                          props.cancel();
                        }
                      },
                      {
                        label: 'Submit ticket',
                        disabled: false,
                        type: ButtonType.PRIMARY,
                        action: async (event, loading) => {
                          formik.setFieldValue('comment', ref.current);
                          try {
                            loading(true);
                            await formik.submitForm();
                          } finally {
                            loading(false);
                          }
                        }
                      }
                    ]}
                  />
                ]
              }
            ]}
          />
        </FormikContext.Provider>
      </S.Container>
    </CenterFormWidget>
  );
});

namespace S {
  export const FormElements = styled.div`
    display: flex;
    flex-direction: column;
    row-gap: 8px;
  `;

  export const Container = styled.div`
    margin: auto;
    margin-bottom: 20px;
    width: 720px;
  `;

  export const TicketReplyContainer = styled.div<{ error?: boolean }>`
    ${CommonInputCss};
    border: solid 1px ${(p) => (p.error ? p.theme.forms.input.error : 'transparent')};
    background: ${(p) => p.theme.forms.input.background};
    color: ${(p) => p.theme.forms.input.color};
    max-height: 300px;
    overflow: auto;
  `;
}
